/**
 * Documentos del vehículo (matrícula, placa, copias): blobs y catálogo.
 *
 * Dos columnas de `producto` guardan listas como JSONs concatenados por '*'
 * (formato heredado del escritorio, congelado desde que solo escribe la web):
 *
 *   - sucursaldocumentos: envoltorio {"documentos": "<json>*<json>*…"} donde
 *     cada <json> es {sucursalrecep, documento, entregado}. Sin documentos
 *     la columna es NULL, nunca envoltorio vacío.
 *   - cajeromatriculaentregada: los mismos JSONs con '*' pero SIN envoltorio;
 *     hay filas históricas con doble encoding (un json.dumps de más del
 *     backend) y como array JSON ([{...}]); el parseo tolera todas.
 *
 * Hasta aquí cada pantalla parseaba a su manera (entrega, consulta, estado,
 * transferencia y devolución: cinco copias con bifurcaciones distintas y
 * bugs propios), y los tipos de documento estaban hardcodeados como
 * placa/matricula. Este módulo es el reemplazo único, espejo de
 * app/documentosvehiculo.py en el server, y el catálogo de tipos ahora vive
 * en la tabla `tipodocumento` por tenant (Configuración → Catálogos).
 *
 * Regla dura: el separador es '*', ningún valor de un documento puede
 * contenerlo (el backend lo valida al guardar el catálogo).
 *
 * Uso:
 *   import { parseDocumentos, useTiposDocumento } from "lib/documentosvehiculo";
 *
 *   const { tipos } = useTiposDocumento();  // [{value:"placa", label:"Placa"}, …]
 *   parseDocumentos(row.sucursaldocumentos) // [{documento, sucursalrecep, entregado}]
 */
import { useState, useEffect, useCallback } from "react";
import { useApi } from "lib/api";

export const SEPARADOR = "*";

/**
 * Tipos con los que responde el server si el tenant aún no tiene catálogo, y
 * fallback local si el endpoint falla (que un error de red nunca deje las
 * pantallas sin checkboxes). Espejo de TIPOS_DEFECTO en el server.
 */
export const TIPOS_DEFECTO = ["placa", "matricula", "copia matricula", "copia placa"];

/**
 * Parsea JSON tolerando basura después del primer objeto o array completo
 * (hay blobs corruptos en producción por escrituras concurrentes); devuelve
 * solo esa primera parte, o null si no hay nada rescatable.
 */
export const safeParse = (str) => {
  if (str == null || typeof str !== "string" || !str.trim()) return null;
  try {
    return JSON.parse(str);
  } catch (e) {
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let i = 0; i < str.length; i++) {
      const c = str[i];
      if (escape) {
        escape = false;
        continue;
      }
      if (inString) {
        if (c === "\\") escape = true;
        else if (c === '"') inString = false;
        continue;
      }
      if (c === '"') inString = true;
      else if (c === "{" || c === "[") depth++;
      else if (c === "}" || c === "]") {
        depth--;
        if (depth === 0) {
          try {
            return JSON.parse(str.slice(0, i + 1));
          } catch (e2) {
            return null;
          }
        }
      }
    }
    return null;
  }
};

/**
 * Lista de documentos del blob `sucursaldocumentos`. null, vacío o corrupto
 * devuelven []; los fragmentos irrescatables se descartan.
 */
export const parseDocumentos = (blob) => {
  const envoltorio = safeParse(blob);
  if (!envoltorio || typeof envoltorio !== "object") return [];
  const interior = envoltorio.documentos;
  if (interior == null || interior === "") return [];
  const items = [];
  String(interior)
    .split(SEPARADOR)
    .forEach((pedazo) => {
      const obj = safeParse(pedazo);
      if (obj && typeof obj === "object" && !Array.isArray(obj)) items.push(obj);
    });
  return items;
};

/**
 * Blob `sucursaldocumentos` listo para guardar (mismo formato que producía
 * entregamatricula), o null si la lista queda vacía — la columna guarda NULL
 * cuando no hay documentos.
 */
export const serializarDocumentos = (items) => {
  const lista = (items || []).filter(Boolean);
  if (!lista.length) return null;
  const interior = lista.map((x) => JSON.stringify(x)).join(SEPARADOR);
  return JSON.stringify({ documentos: interior });
};

/**
 * Lista de entregas de `cajeromatriculaentregada` (formato sin envoltorio).
 * Tolera las cuatro formas reales: objeto solo, N objetos con '*', array
 * JSON, y el doble encoding histórico.
 */
export const parseEntregas = (valor) => {
  if (valor == null) return [];
  let texto = String(valor).trim();
  // Des-encodar el string JSON exterior mientras exista ("{\"a\":1}*…").
  for (let i = 0; i < 2; i++) {
    if (!texto.startsWith('"')) break;
    try {
      texto = String(JSON.parse(texto));
    } catch (e) {
      // Fragmento con comillas rotas: quitar la exterior y desescapar.
      texto = texto.replace(/^"|"$/g, "").replace(/\\"/g, '"');
      break;
    }
  }
  if (!texto) return [];
  if (!texto.includes(SEPARADOR)) {
    let obj = safeParse(texto);
    if (typeof obj === "string") obj = safeParse(obj);
    if (Array.isArray(obj)) return obj.filter((x) => x && typeof x === "object");
    if (obj && typeof obj === "object") return [obj];
    return [];
  }
  const items = [];
  texto.split(SEPARADOR).forEach((pedazo) => {
    let obj = safeParse(pedazo);
    if (typeof obj === "string") obj = safeParse(obj);
    if (obj && typeof obj === "object" && !Array.isArray(obj)) items.push(obj);
  });
  return items;
};

/** Valor de `cajeromatriculaentregada` listo para guardar, o null si vacío. */
export const serializarEntregas = (items) => {
  const lista = (items || []).filter(Boolean);
  if (!lista.length) return null;
  return lista.map((x) => JSON.stringify(x)).join(SEPARADOR);
};

/** Entregas existentes + la nueva, en el formato de guardado. */
export const appendEntrega = (valor, entrega) =>
  serializarEntregas([...parseEntregas(valor), entrega]);

/**
 * Código de sucursal de un valor en formato mixto ("2" o "2-Veron"): todas
 * las comparaciones por sucursal se hacen por código, nunca por label.
 */
export const codigoSucursal = (valor) => String(valor || "").split("-")[0].trim();

/** Nombre de la sucursal si el valor lo trae ("2-Veron" → "Veron"). */
export const nombreSucursal = (valor) => {
  const partes = String(valor || "").split("-");
  return partes.length > 1 ? partes.slice(1).join("-").trim() : "";
};

/**
 * Estado del blob por tipo del catálogo:
 *   { [tipo]: { presente, entregado, sucursalrecep } }
 * `presente` = el documento está ubicado en una sucursal (está en el blob:
 * lo escribe la recepción DGII y lo mueve la recepción de envíos);
 * `entregado` = ya se entregó al cliente. Match por igualdad exacta
 * del tipo — nunca includes(): "matricula" es prefijo de otros tipos.
 */
export const estadoPorTipo = (blob, tipos) => {
  const documentos = parseDocumentos(blob);
  const estado = {};
  (tipos || []).forEach((tipo) => {
    const value = typeof tipo === "string" ? tipo : tipo.value;
    const doc = documentos.find((d) => d.documento === value);
    estado[value] = {
      presente: Boolean(doc),
      entregado: Boolean(doc && doc.entregado),
      sucursalrecep: doc ? doc.sucursalrecep || "" : "",
    };
  });
  return estado;
};

/** "copia matricula" → "Copia Matricula" (los labels legados capitalizaban). */
export const labelTipo = (value) =>
  String(value || "")
    .split(" ")
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(" ");

/**
 * Catálogo de tipos de documento del tenant, en la forma que consumen los
 * checkboxes y selectores de las pantallas: [{ value, label, precio }].
 * `precio` es la tarifa opcional (0 = sin tarifa) que el cobro PROPONE,
 * nunca obliga. Si el endpoint falla se usan los tipos por defecto — las
 * pantallas nunca quedan sin opciones.
 */
export function useTiposDocumento() {
  const { post } = useApi();
  const [tipos, setTipos] = useState(
    TIPOS_DEFECTO.map((t) => ({ value: t, label: labelTipo(t), precio: 0 }))
  );
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/tiposdocumento_listar", {});
      if (ok && Array.isArray(data.tipos) && data.tipos.length) {
        const precios = data.precios || {};
        setTipos(
          data.tipos.map((t) => ({
            value: t,
            label: labelTipo(t),
            precio: Number(precios[t]) > 0 ? Number(precios[t]) : 0,
          }))
        );
      }
    } catch (e) {
      // fallback: se quedan los tipos por defecto
    } finally {
      setCargando(false);
    }
  }, [post]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return { tipos, cargando, recargar: cargar };
}
